import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { ThemeMode } from './tokens';
import { createAppTheme } from '../theme';
import { applyHighchartsTheme } from './highchartsTheme';

const STORAGE_KEY = 'remus-theme';

interface ThemeModeContextValue {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

/**
 * Resolve the initial mode: an explicit stored choice wins, otherwise we
 * honour the OS `prefers-color-scheme`. Kept in sync with the tiny inline
 * bootstrap in index.html (which sets data-theme before first paint to
 * avoid a light flash).
 */
export const getStoredThemeMode = (): ThemeMode => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // localStorage can throw in private-mode / sandboxed iframes — fall through.
  }
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

/**
 * Owns the active colour mode for the whole app:
 *  - flips the `data-theme` attribute on <html> (drives every --ds-* token),
 *  - rebuilds the MUI theme for the mode,
 *  - re-applies the Highcharts theme to all mounted charts,
 *  - persists the choice to localStorage.
 *
 * Mounted near the root, above KeycloakProvider/App, replacing the bare
 * MUI ThemeProvider that used to live in index.jsx.
 */
export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(getStoredThemeMode);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Persistence is best-effort; the attribute above is what matters.
    }
    // Charts read the freshly-applied CSS variables, so run after the
    // attribute is set (useLayoutEffect → before paint).
    applyHighchartsTheme(mode);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => setModeState(next), []);
  const toggleMode = useCallback(
    () => setModeState(prev => (prev === 'dark' ? 'light' : 'dark')),
    []
  );

  const muiTheme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({ mode, isDark: mode === 'dark', setMode, toggleMode }),
    [mode, setMode, toggleMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

/** Read/control the active colour mode. Throws outside the provider. */
export const useThemeMode = (): ThemeModeContextValue => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return ctx;
};

export default ThemeModeProvider;
